import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Switch, Tooltip, Button } from "@mui/material";

/**
 * シナリオの配信マップ（マインドマップ表示）。
 *
 *   フェーズ（親ノード） ──曲線── メッセージ（子ノード・上から時系列）
 *
 * ⚠️ なぜ「ステータス別のレーン」をやめたか:
 *   target_tag（お客様の状態）は**表示・整理用のラベル**で、実際に誰へ届くかは
 *   きっかけと送信条件が決める（API 側のコメントにも明記されている）。
 *   レーンで区切ると「この状態の人にだけ届く」と読めてしまううえ、同じ流れの中の
 *   メッセージ（体験前→当日→追客）が別々の箱に散って**流れが追えなかった**。
 *   ここでは管理番号の頭の数字＝運用上のフェーズでまとめ、状態はノードに出さない。
 *
 * ⚠️ 縦の並び順に「起点」を混ぜてはいけない:
 *   同じフェーズでも基準の日が違う（1-1 は申込時、1-2/1-3 は体験日）。日数だけで並べると
 *   別の日を基準にした日数を同じ物差しで比べることになり嘘になる。
 *   **起点ごとにまとめてから**日数で並べ、起点はノードにそのまま出す。
 *
 * ⚠️ 時間軸の位置は API の `axis` をそのまま使う（trigger から推測しない）。
 *   scan 系は DB の offset_days が 0 で実際の日数はコード側にあるため、推測すると
 *   「引落しの3日前」を『当日』と表示してしまう。
 */

// 管理番号の頭の数字＝運用上のフェーズ。番号体系はコード側（scenario_seed）の慣習に合わせる。
const PHASES = [
  { key: "1", label: "体験・予約フェーズ" },
  { key: "2", label: "入会オファー" },
  { key: "3", label: "追客・長期フォロー" },
  { key: "4", label: "入会・離脱防止" },
  { key: "5", label: "欠席・継続対策" },
  { key: "6", label: "お支払い・請求" },
  { key: "7", label: "退会・復帰" },
  { key: "8", label: "紹介・口コミ" },
];
const PHASE_LABEL = {};
PHASES.forEach((p) => { PHASE_LABEL[p.key] = p.label; });

/** 管理番号（"2-1b" など）の頭の数字。付いていないものは「その他」へ。 */
function phaseKeyOf(sid) {
  const m = String(sid || "").match(/^(\d+)/);
  return m ? m[1] : "_other";
}

/** 管理番号は "2-1" と "2-1b" と "2-10" が混ざるので数値混じりで比べる。 */
function cmpId(a, b) {
  return String(a).localeCompare(String(b), "ja", { numeric: true });
}

/**
 * 「前日 18:00」のように短く言い直す。
 * API の label（"−1日 18:00"）をそのまま出すと、日付の前後が読み取りにくいため。
 */
function shortTiming(s) {
  const a = s.axis || {};
  if (!a.label) return "";
  if (a.variable) return a.label; // 毎月N日（起点からの日数が毎回変わる）
  if (a.label === "即時") return "即時";
  const at = (a.label.match(/\d{1,2}:\d{2}/) || [""])[0];
  const tail = a.label.endsWith("〜") ? "〜" : "";
  const d = a.days;
  const day =
    d == null ? ""
      : d === 0 ? "当日"
        : d === -1 ? "前日"
          : d === 1 ? "翌日"
            : d > 0 ? `${d}日後` : `${Math.abs(d)}日前`;
  return `${day}${at ? ` ${at}` : ""}${tail}`.trim();
}

/** 送信条件を「まだ入会していない方・来なかった」の形に短縮。 */
function shortConditions(conds, fields) {
  if (!conds || !fields || !fields.length) return "";
  const parts = [];
  fields.forEach((f) => {
    if (!(f.key in conds)) return;
    const opt = f.options.find((o) => JSON.stringify(o.value) === JSON.stringify(conds[f.key]));
    if (opt) parts.push(opt.label.replace(/（.*?）/g, "").trim());
  });
  return parts.join("・");
}

/** 起点ごとにまとめ、その中を日数 → 判定順 → 番号で並べる。 */
function sortItems(items) {
  // 起点の並びは「その起点で一番若い管理番号」で決める（1-1 の申込時 → 1-2 の体験日）
  const originHead = {};
  items.forEach((s) => {
    const o = s.origin || "";
    if (!(o in originHead) || cmpId(s.scenario_id, originHead[o]) < 0) originHead[o] = s.scenario_id;
  });
  const dayOf = (s) => (s.axis && s.axis.days != null ? s.axis.days : 99999);
  return items.slice().sort((x, y) => {
    const ox = x.origin || "";
    const oy = y.origin || "";
    if (ox !== oy) return cmpId(originHead[ox], originHead[oy]);
    if (dayOf(x) !== dayOf(y)) return dayOf(x) - dayOf(y);
    if ((x.position ?? 0) !== (y.position ?? 0)) return (x.position ?? 0) - (y.position ?? 0);
    return cmpId(x.scenario_id, y.scenario_id);
  });
}

function buildBranches(list) {
  const byPhase = {};
  (list || []).forEach((s) => {
    const k = phaseKeyOf(s.scenario_id);
    (byPhase[k] = byPhase[k] || []).push(s);
  });
  const known = PHASES.map((p) => p.key).filter((k) => byPhase[k]);
  const rest = Object.keys(byPhase).filter((k) => !PHASE_LABEL[k]).sort(cmpId);
  return [...known, ...rest].map((key) => {
    const items = sortItems(byPhase[key]);
    return {
      key,
      // 番号体系にない枝（9-1 のテスト用など）も番号は出す。全部「その他」だと見分けがつかない。
      title: PHASE_LABEL[key] ? `${key}. ${PHASE_LABEL[key]}`
        : key === "_other" ? "その他" : `${key}. その他`,
      items,
      total: items.length,
      live: items.filter((s) => s.enabled).length,
    };
  });
}

function ScenarioMindMap({
  list, anchors, conditionFields,
  selectedId, onSelect, onToggleEnabled, onAdd,
}) {
  const canvasRef = useRef(null);
  const rootRefs = useRef({});
  const nodeRefs = useRef({});
  // 開いているフェーズ。**既定は全部閉じた状態**（27件を全部広げると縦に長く、
  // どのフェーズがあるのか一覧できない）。見たい枝だけ開いてもらう。
  const [opened, setOpened] = useState({});
  const [paths, setPaths] = useState([]);

  const branches = useMemo(() => buildBranches(list), [list]);
  const anchorInfo = useMemo(() => {
    const m = {};
    (anchors || []).forEach((a) => { m[a.value] = a; });
    return m;
  }, [anchors]);

  /**
   * 親ノードの右端から子ノードの左端へベジェ曲線を引く。
   *
   * ⚠️ 線は CSS では引けない（親と子の縦位置は中身の高さで決まるため）。
   * 描画後に実際の座標を測って SVG に落とす。canvas は fit-content なので
   * 絶対配置の SVG（inset:0）が横スクロール分まで覆う。
   */
  const measure = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const base = canvas.getBoundingClientRect();
    const next = [];
    branches.forEach((b) => {
      const rootEl = rootRefs.current[b.key];
      if (!rootEl) return;
      const r = rootEl.getBoundingClientRect();
      const x1 = r.right - base.left;
      const y1 = r.top + r.height / 2 - base.top;
      b.items.forEach((s) => {
        const el = nodeRefs.current[s.scenario_id];
        if (!el) return;
        const c = el.getBoundingClientRect();
        const x2 = c.left - base.left;
        const y2 = c.top + c.height / 2 - base.top;
        const dx = Math.max(20, (x2 - x1) * 0.55);
        next.push({
          key: `${b.key}>${s.scenario_id}`,
          d: `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`,
          on: selectedId === s.scenario_id,
          off: !s.enabled,
        });
      });
    });
    setPaths(next);
  }, [branches, selectedId]);

  useLayoutEffect(() => { measure(); }, [measure, opened]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(canvas);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const setNodeRef = (id) => (el) => {
    if (el) nodeRefs.current[id] = el;
    else delete nodeRefs.current[id];
  };

  const allClosed = branches.every((b) => !opened[b.key]);
  const toggleAll = () => {
    if (!allClosed) return setOpened({});
    const next = {};
    branches.forEach((b) => { next[b.key] = true; });
    setOpened(next);
  };

  return (
    <div className="sm-mm">
      <div className="sm-mm-toolbar">
        <span className="sm-mm-hint">
          フェーズごとに、上から送られる順に並んでいます。クリックすると右側で編集できます。
        </span>
        <Button size="small" onClick={toggleAll}>{allClosed ? "すべて開く" : "すべて閉じる"}</Button>
        <Button size="small" variant="outlined" onClick={() => onAdd()}>＋ メッセージを追加</Button>
      </div>

      <div className="sm-mm-scroll">
        <div className="sm-mm-canvas" ref={canvasRef}>
          <svg className="sm-mm-lines" aria-hidden="true">
            {paths.map((p) => (
              <path
                key={p.key}
                d={p.d}
                className={`sm-mm-line ${p.on ? "on" : ""} ${p.off ? "off" : ""}`}
              />
            ))}
          </svg>

          {branches.length === 0 && (
            <div className="sm-mm-empty">メッセージがまだありません。</div>
          )}

          {branches.map((b) => {
            const open = !!opened[b.key];
            return (
              <div className="sm-mm-branch" key={b.key}>
                <div className="sm-mm-rootcol">
                  <div className={`sm-mm-root p${b.key}`} ref={(el) => { rootRefs.current[b.key] = el; }}>
                    <span className="sm-mm-root-title">{b.title}</span>
                    <Tooltip title="配信中 / 全体">
                      <span className="sm-mm-root-count">{b.live}/{b.total}</span>
                    </Tooltip>
                    <Tooltip title={open ? "この枝を閉じる" : "この枝を開く"}>
                      <button
                        type="button"
                        className="sm-mm-toggle"
                        aria-expanded={open}
                        onClick={() => setOpened((o) => ({ ...o, [b.key]: !open }))}
                      >
                        {open ? "‹" : "›"}
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {open && (
                  <div className="sm-mm-children">
                    {b.items.map((s) => {
                      const excl = !!anchorInfo[s.anchor]?.exclusive;
                      const cond = shortConditions(s.send_conditions, (conditionFields || {})[s.anchor]);
                      const timing = shortTiming(s);
                      return (
                        <div
                          key={s.scenario_id}
                          ref={setNodeRef(s.scenario_id)}
                          className={`sm-mm-node ${selectedId === s.scenario_id ? "on" : ""} ${s.enabled ? "" : "off"}`}
                        >
                          <button type="button" className="sm-mm-node-btn" onClick={() => onSelect(s.scenario_id)}>
                            <span className="sm-mm-node-head">
                              <span className="sm-mm-node-id">{s.scenario_id}</span>
                              <span className="sm-mm-node-name">{s.name}</span>
                            </span>
                            <span className="sm-mm-node-meta">
                              {s.origin ? (
                                <span className="sm-mm-tag">起点 {s.origin}</span>
                              ) : (
                                <Tooltip title="日時を決めて送るのではなく、その出来事が起きたときに送られます">
                                  <span className="sm-mm-tag">随時</span>
                                </Tooltip>
                              )}
                              {timing && <span className="sm-mm-tag time">{timing}</span>}
                              {s.axis?.variable && <span className="sm-mm-tag var">可変</span>}
                              {cond && <span className="sm-mm-tag cond">{cond}</span>}
                              {excl && (
                                <Tooltip title={`「${anchorInfo[s.anchor]?.label || s.anchor}」では、条件に合うものが1通だけ送られます`}>
                                  <span className="sm-mm-tag excl">どれか1通</span>
                                </Tooltip>
                              )}
                              {!s.enabled && <span className="sm-mm-tag stop">停止中</span>}
                            </span>
                          </button>
                          <Tooltip title={s.enabled ? "配信中（クリックで停止）" : "停止中（クリックで配信開始）"}>
                            <Switch size="small" checked={s.enabled} onChange={() => onToggleEnabled(s)} />
                          </Tooltip>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ScenarioMindMap;
